import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, Copy, Info, Loader2, Sparkles, X } from 'lucide-react';

import { assistHandbuch, assistKoordinierung, assistSigePlan } from '../../services/geminiService';
import type {
    AiAssistErgebnis,
    HandbuchAssistAntwort,
    KoordinierungAssistAntwort,
    PlanAssistAntwort,
} from '../../services/aiAssistContext';
import {
    ORDNUNG_FELD_LABEL,
    anzahlHandbuch,
    anzahlKoordinierung,
    anzahlPlan,
    entwuerfeAus,
    fasseZusammen,
    findeErweiterung,
    EINRICHTUNG_FELD_LABEL,
    mischeEinrichtung,
    mischeOrdnung,
    nutzbareAntwort,
    offeneEinrichtungsPunkte,
    offeneOrdnungsPunkte,
    ordnungsLeerMeldung,
    planeKoordinierungUebernahme,
    type AntwortLage,
    type KiBruecke,
    type KiOeffnung,
    type KiReiter,
    type EinrichtungHerkunft,
    type OrdnungFeld,
    type OrdnungHerkunft,
} from './kiAssistentLogic';

interface Props {
    /** Auf welchem Reiter das Fenster geöffnet wurde. */
    reiter: KiReiter;
    /** Der Draht zum Reiter. Fehlt er, kann das Fenster nur erklären, warum. */
    bruecke: KiBruecke | null;
    onSchliessen: () => void;
    /** Sprung an die passende Stelle — nur der Plan-Reiter benutzt ihn. */
    onSprung?: (ziel: KiReiter) => void;
}

/** Der Antwortstand, nach Reiter getrennt — drei Formen, ein Zustand. */
type AntwortStand =
    | { art: 'plan'; ergebnis: AiAssistErgebnis<PlanAssistAntwort> }
    | { art: 'koordinierung'; ergebnis: AiAssistErgebnis<KoordinierungAssistAntwort> }
    | { art: 'handbuch'; ergebnis: AiAssistErgebnis<HandbuchAssistAntwort> };

const REITER_TITEL: Record<KiReiter, string> = {
    plan: 'SiGe-Plan',
    koordinierung: 'Gewerke-Überschneidung',
    handbuch: 'SiGe-Handbuch',
};

/** Was der Assistent auf diesem Reiter tut — steht als erster Satz im Fenster. */
const REITER_ZWECK: Record<KiReiter, string> = {
    plan: 'Fragen zum Plan als Ganzem: was zwischen den Arbeitsschritten fehlt. '
        + 'Übernommen wird hier NICHTS: der Plan gehört dem Editor, das Fenster zeigt nur.',
    koordinierung: 'Eine zweite Stimme NEBEN dem Regelkatalog. Der Katalog bleibt die Grundlage; '
        + 'angenommene Vorschläge werden unbestätigte Bewertungen mit Herkunft „KI".',
    handbuch: 'Textvorschläge für die Baustellenordnung und Hinweise auf Fehlstellen. '
        + 'Übernommenes landet im Entwurf, gespeichert wird es erst von Ihnen.',
};

const LAGE_FARBE: Record<AntwortLage, string> = {
    unbenutzt: 'bg-slate-50 border-slate-200 text-slate-600',
    laeuft: 'bg-blue-50 border-blue-200 text-blue-900',
    nicht_freigeschaltet: 'bg-amber-50 border-amber-200 text-amber-900',
    fehler: 'bg-rose-50 border-rose-200 text-rose-800',
    leer: 'bg-slate-50 border-slate-200 text-slate-600',
    treffer: 'bg-emerald-50 border-emerald-200 text-emerald-900',
};

const PRIO_FARBE: Record<string, string> = {
    hoch: 'text-rose-600',
    mittel: 'text-amber-600',
    niedrig: 'text-slate-500',
};

/** Ein Wert in einer Menge an- oder abwählen. */
const wechsle = (menge: string[], wert: string): string[] =>
    (menge.includes(wert) ? menge.filter(w => w !== wert) : [...menge, wert]);

/** Ein Eintrag der Vorschlagsliste. Ausserhalb der Komponente, damit React die
 *  Kästen beim Zeichnen nicht jedes Mal neu einhängt. */
const Kasten: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-3">{children}</div>
);

const Haken: React.FC<{ an: boolean; onWechsel: () => void; text: React.ReactNode; aus?: boolean }> =
    ({ an, onWechsel, text, aus }) => (
        <label className={`flex items-start gap-2 text-sm ${aus ? 'opacity-50' : 'cursor-pointer'}`}>
            <input
                type="checkbox"
                checked={an}
                disabled={aus}
                onChange={onWechsel}
                className="mt-0.5 shrink-0 w-4 h-4 accent-blue-600"
            />
            <span className="min-w-0 flex-1 text-slate-700">{text}</span>
        </label>
    );

/**
 * Das gemeinsame KI-Assistenzfenster der drei SiGe-Plan-Reiter.
 *
 * ═══ VORSCHLAGEN, PRÜFEN, ERST DANN ÜBERNEHMEN ═════════════════════════════
 * Das Fenster ruft beim Öffnen KEINE KI. Es baut nur den Kontext des aktiven
 * Reiters (der Reiter selbst tut das, siehe `KiBruecke`), wartet auf einen
 * Knopfdruck, legt die Antwort in den Zustand und zeigt sie an. Übernommen
 * wird ausschließlich, was der Anwender anhakt. Hausmuster dafür ist
 * `HazardMatrixView` (Ergebnis in den Zustand, eigene Vorschau, Übernahme erst
 * auf Klick) — hier mit einer Verschärfung: vorbelegt ist NICHTS. Ein
 * Katalogtreffer ist eine Regel, eine KI-Antwort ist eine Meinung; die darf
 * nicht mit einem Klick vollständig in einen SiGe-Plan wandern.
 *
 * ═══ WAS DAS FENSTER NICHT TUT ═════════════════════════════════════════════
 * Es schreibt nirgends selbst. Jede Übernahme geht durch `bruecke.uebernimm`
 * in den vorhandenen Weg des Reiters — dort steht der Schreibschutz, dort
 * kennt das Modul seinen eigenen Zustand, und dort sieht der Anwender das
 * Ergebnis sofort in der Liste, die er ohnehin vor sich hat.
 */
export const KiAssistent: React.FC<Props> = ({ reiter, bruecke, onSchliessen, onSprung }) => {
    const [oeffnung, setOeffnung] = useState<KiOeffnung | null>(null);
    const [kontextLaeuft, setKontextLaeuft] = useState(true);
    const [kontextFehler, setKontextFehler] = useState<string | null>(null);

    const [frage, setFrage] = useState('');
    const [laeuft, setLaeuft] = useState(false);
    const [stand, setStand] = useState<AntwortStand | null>(null);

    const [aktionsfehler, setAktionsfehler] = useState<string | null>(null);
    const [meldung, setMeldung] = useState<string | null>(null);

    // Auswahl der Koordinierungs-Vorschläge (ids) und der Handbuch-Punkte.
    const [gewaehltePaare, setGewaehltePaare] = useState<string[]>([]);
    const [gewaehlteFelder, setGewaehlteFelder] = useState<string[]>([]);
    const [gewaehlteRegeln, setGewaehlteRegeln] = useState<string[]>([]);
    const [gewaehlteSchritte, setGewaehlteSchritte] = useState<string[]>([]);
    /** Die angehakten FELDER der Baustelleneinrichtung — eigene Auswahl, weil
     *  sie getrennt von der Baustellenordnung uebernommen werden. */
    const [gewaehlteEinrichtung, setGewaehlteEinrichtung] = useState<string[]>([]);

    /** Was bereits übernommen wurde — verhindert das zweite Anlegen. */
    const [uebernommenePaare, setUebernommenePaare] = useState<string[]>([]);
    const [uebernommeneOrdnung, setUebernommeneOrdnung] = useState<OrdnungHerkunft | null>(null);
    const [uebernommeneEinrichtung, setUebernommeneEinrichtung] =
        useState<EinrichtungHerkunft | null>(null);

    // ─── Tastatur: die Auflage muss ohne Maus wieder zugehen ──────────────
    // Sie trägt `role="dialog"` und legt sich über die ganze Ansicht; ohne
    // Escape käme man mit der Tastatur nicht mehr heraus. Über eine Referenz,
    // damit der Listener nicht bei jedem Tastendruck neu registriert wird
    // (Muster wie in `CompanyMergeAssistant`).
    const schliessenRef = useRef(onSchliessen);
    useEffect(() => { schliessenRef.current = onSchliessen; });
    useEffect(() => {
        const beiTaste = (e: KeyboardEvent) => {
            if (e.key === 'Escape') schliessenRef.current();
        };
        window.addEventListener('keydown', beiTaste);
        return () => window.removeEventListener('keydown', beiTaste);
    }, []);

    // Anfangsfokus in die Auflage: sonst steht er weiter in der Liste dahinter,
    // und die erste Tabulatortaste führte hinter das Fenster statt hinein.
    const auflageRef = useRef<HTMLElement>(null);
    useEffect(() => { auflageRef.current?.focus(); }, []);

    // ─── Kontext: EINMAL beim Öffnen, nicht auf Vorrat ────────────────────
    useEffect(() => {
        let verworfen = false;
        setKontextLaeuft(true);
        setKontextFehler(null);
        if (!bruecke) {
            setKontextFehler('Dieser Reiter stellt dem Assistenten gerade keine Daten bereit. '
                + 'Bitte den Reiter neu öffnen.');
            setKontextLaeuft(false);
            return;
        }
        bruecke.oeffne()
            .then(o => { if (!verworfen) setOeffnung(o); })
            .catch(e => {
                if (!verworfen) {
                    setKontextFehler(e instanceof Error ? e.message : String(e));
                }
            })
            .finally(() => { if (!verworfen) setKontextLaeuft(false); });
        return () => { verworfen = true; };
    }, [bruecke]);

    const paket = oeffnung ? oeffnung.paket : null;
    const darfUebernehmen = !!oeffnung && oeffnung.darfUebernehmen;

    // ─── Fragen ──────────────────────────────────────────────────────────

    const frageStellen = useCallback(async () => {
        if (!paket || laeuft) return;
        setLaeuft(true);
        setAktionsfehler(null);
        setMeldung(null);
        setGewaehltePaare([]);
        setGewaehlteFelder([]);
        setGewaehlteRegeln([]);
        setGewaehlteSchritte([]);
        setGewaehlteEinrichtung([]);
        try {
            if (paket.art === 'plan') {
                setStand({ art: 'plan', ergebnis: await assistSigePlan(paket.kontext, frage.trim()) });
            } else if (paket.art === 'koordinierung') {
                setStand({ art: 'koordinierung', ergebnis: await assistKoordinierung(paket.kontext) });
            } else {
                setStand({ art: 'handbuch', ergebnis: await assistHandbuch(paket.kontext, frage.trim()) });
            }
        } catch (e) {
            // Die drei Assistenten geben einen Umschlag zurück statt zu werfen.
            // Kommt hier trotzdem etwas an, ist es ein Fehler VOR dem Aufruf —
            // er darf das Fenster nicht stumm lassen.
            setAktionsfehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaeuft(false);
        }
    }, [paket, laeuft, frage]);

    // ─── Ableitungen ─────────────────────────────────────────────────────

    const planAntwort = stand && stand.art === 'plan'
        ? nutzbareAntwort<PlanAssistAntwort>(stand.ergebnis) : null;
    const koordAntwort = stand && stand.art === 'koordinierung'
        ? nutzbareAntwort<KoordinierungAssistAntwort>(stand.ergebnis) : null;
    const handbuchAntwort = stand && stand.art === 'handbuch'
        ? nutzbareAntwort<HandbuchAssistAntwort>(stand.ergebnis) : null;

    const anzahl = planAntwort ? anzahlPlan(planAntwort)
        : koordAntwort ? anzahlKoordinierung(koordAntwort)
            : anzahlHandbuch(handbuchAntwort);

    const zusammenfassung = useMemo(
        () => fasseZusammen(stand ? stand.ergebnis : null, anzahl, laeuft),
        [stand, anzahl, laeuft],
    );

    /** Vorschau der Koordinierungs-Übernahme — dieselbe reine Funktion, die
     *  anschließend die Entwürfe liefert. */
    const geplant = useMemo(() => {
        if (!paket || paket.art !== 'koordinierung') return [];
        return planeKoordinierungUebernahme(koordAntwort, paket.zeilen, {
            sigeProjektId: paket.sigeProjektId,
        });
    }, [paket, koordAntwort]);

    const offeneOrdnung = useMemo(() => {
        if (!paket || paket.art !== 'handbuch') return null;
        return offeneOrdnungsPunkte(paket.bestand, handbuchAntwort ? handbuchAntwort.ordnung : null);
    }, [paket, handbuchAntwort]);

    const offeneEinrichtung = useMemo(() => {
        if (!paket || paket.art !== 'handbuch') return null;
        return offeneEinrichtungsPunkte(
            paket.bestandEinrichtung,
            handbuchAntwort ? handbuchAntwort.einrichtung : null,
        );
    }, [paket, handbuchAntwort]);

    const neuePaare = geplant.filter(g => g.befund === 'neu' && !uebernommenePaare.includes(g.id));

    /**
     * Was der Satz „nichts zu ergänzen" wert ist.
     *
     * Die Kappungsangabe kommt aus dem Umschlag; fehlt sie, entwarnt
     * `ordnungsLeerMeldung` bewusst NICHT (siehe dort).
     */
    const leereOrdnung = ordnungsLeerMeldung(
        stand && stand.art === 'handbuch' ? stand.ergebnis.unvollstaendig : undefined,
    );

    /**
     * Zu welcher vorhandenen Zeile ein angebotener Punkt gehört.
     *
     * Ein Vorschlag, der eine Bestandszeile wörtlich fortschreibt, ist kein
     * Doppel und bleibt wählbar — nach der Übernahme stünden aber beide
     * nebeneinander. Der Bezug macht das am Haken sichtbar, damit der
     * Gegenleser die kürzere Fassung bewusst behält oder streicht.
     */
    const erweiterungen = useMemo(() => {
        const bezug = new Map<string, string>();
        if (!paket || paket.art !== 'handbuch' || !offeneOrdnung) return bezug;
        for (const [neue, alte] of [
            [offeneOrdnung.regeln, paket.bestand?.regeln || []],
            [offeneOrdnung.notfallschritte, paket.bestand?.notfallschritte || []],
        ] as const) {
            for (const zeile of neue) {
                const alt = findeErweiterung(zeile, alte);
                if (alt) bezug.set(zeile, alt);
            }
        }
        return bezug;
    }, [paket, offeneOrdnung]);

    // ─── Übernehmen ──────────────────────────────────────────────────────

    const uebernimmPaare = async () => {
        if (!bruecke || laeuft) return;
        const entwuerfe = entwuerfeAus(neuePaare, new Set(gewaehltePaare));
        if (!entwuerfe.length) return;
        setLaeuft(true);
        setAktionsfehler(null);
        try {
            setMeldung(await bruecke.uebernimm({ art: 'koordinierung', entwuerfe }));
            setUebernommenePaare(alt => [...alt, ...gewaehltePaare]);
            setGewaehltePaare([]);
        } catch (e) {
            setAktionsfehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaeuft(false);
        }
    };

    const uebernimmOrdnung = async () => {
        if (!bruecke || !paket || paket.art !== 'handbuch' || !handbuchAntwort || laeuft) return;
        const { entwurf, herkunft } = mischeOrdnung(paket.bestand, handbuchAntwort.ordnung, {
            felder: gewaehlteFelder,
            regeln: gewaehlteRegeln,
            notfallschritte: gewaehlteSchritte,
        });
        if (herkunft.anzahl === 0) return;
        setLaeuft(true);
        setAktionsfehler(null);
        try {
            setMeldung(await bruecke.uebernimm({ art: 'handbuch', ordnung: entwurf, herkunft }));
            setUebernommeneOrdnung(herkunft);
        } catch (e) {
            setAktionsfehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaeuft(false);
        }
    };

    /**
     * Übernimmt den Einrichtungs-Vorschlag — ein EIGENER Aufruf.
     *
     * Ordnung und Einrichtung gehen getrennt über die Brücke, weil sie im
     * Handbuchmodul zwei getrennte Entwürfe mit zwei getrennten
     * Herkunftsnachweisen sind. Ein gemeinsamer Aufruf behauptete im blauen
     * Balken, auch der nicht angetastete Teil stamme aus der KI.
     */
    const uebernimmEinrichtung = async () => {
        if (!bruecke || !paket || paket.art !== 'handbuch' || !handbuchAntwort || laeuft) return;
        const { entwurf, herkunft } = mischeEinrichtung(
            paket.bestandEinrichtung, handbuchAntwort.einrichtung, gewaehlteEinrichtung,
        );
        if (herkunft.anzahl === 0) return;
        setLaeuft(true);
        setAktionsfehler(null);
        try {
            setMeldung(await bruecke.uebernimm({
                art: 'handbuch-einrichtung', einrichtung: entwurf, herkunft,
            }));
            setUebernommeneEinrichtung(herkunft);
        } catch (e) {
            setAktionsfehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaeuft(false);
        }
    };

    /**
     * Der Knopf im Fuß des Handbuch-Reiters.
     *
     * Er bedient beide Teile — nacheinander und nur den, zu dem etwas
     * angehakt ist. Zwei Knöpfe nebeneinander wären die ehrlichere Abbildung
     * der zwei Wege, aber der Fuß trägt einen, und ein Anwender, der beides
     * angehakt hat, erwartet nach einem Klick auch beides im Entwurf.
     */
    const uebernimmHandbuch = async () => {
        if (gewaehlteFelder.length || gewaehlteRegeln.length || gewaehlteSchritte.length) {
            await uebernimmOrdnung();
        }
        if (gewaehlteEinrichtung.length) await uebernimmEinrichtung();
    };

    /** Text in die Zwischenablage — der Plan-Reiter übernimmt nicht, aber der
     *  Bearbeiter soll den Wortlaut nicht abtippen müssen. */
    const kopiere = (text: string) => {
        void navigator.clipboard?.writeText(text)
            .then(() => setMeldung('In die Zwischenablage kopiert.'))
            .catch(() => setAktionsfehler('Kopieren hat nicht geklappt. Bitte von Hand markieren.'));
    };

    // ─── Bausteine ───────────────────────────────────────────────────────

    const alleWaehlen = () => {
        if (paket && paket.art === 'koordinierung') {
            setGewaehltePaare(neuePaare.map(g => g.id));
            return;
        }
        if (offeneOrdnung) {
            setGewaehlteFelder(offeneOrdnung.felder);
            setGewaehlteRegeln(offeneOrdnung.regeln);
            setGewaehlteSchritte(offeneOrdnung.notfallschritte);
        }
        if (offeneEinrichtung) {
            setGewaehlteEinrichtung(offeneEinrichtung.punkte.map(p => p.feld));
        }
    };

    const anzahlGewaehlt = paket && paket.art === 'koordinierung'
        ? gewaehltePaare.length
        : gewaehlteFelder.length + gewaehlteRegeln.length + gewaehlteSchritte.length
            + gewaehlteEinrichtung.length;

    // ═══════════════════════════════════════════════════════════════════════

    return (
        <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-label="KI-Assistent">
            {/* Der Hintergrund bleibt sichtbar: der Anwender soll die Liste
                sehen, über die der Assistent spricht. */}
            <button
                type="button"
                aria-label="Assistent schließen"
                onClick={onSchliessen}
                className="flex-1 bg-slate-900/20"
            />
            <aside
                ref={auflageRef}
                tabIndex={-1}
                className="w-full sm:w-[30rem] max-w-full bg-slate-50 border-l border-slate-200 shadow-2xl flex flex-col min-h-0 focus:outline-none"
            >

                {/* ─── Kopf ──────────────────────────────────────────────── */}
                <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-start gap-3 shrink-0">
                    <div className="mt-0.5 w-8 h-8 rounded-lg bg-gradient-to-br from-violet-50 to-violet-100 border border-violet-100 flex items-center justify-center shrink-0">
                        <Sparkles className="w-4 h-4 text-violet-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 className="text-sm font-black text-slate-900 leading-tight">
                            KI-Assistent · {REITER_TITEL[reiter]}
                        </h2>
                        <p className="text-[11px] text-slate-500 mt-0.5">{REITER_ZWECK[reiter]}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onSchliessen}
                        aria-label="Schließen"
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 shrink-0"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto min-h-0 px-4 py-4 space-y-3">

                    {/* ─── Kontext ───────────────────────────────────────── */}
                    {kontextLaeuft && (
                        <p className="text-sm text-slate-400 py-6 text-center">
                            Der Stand dieses Reiters wird zusammengestellt …
                        </p>
                    )}
                    {kontextFehler && (
                        <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800">
                            {kontextFehler}
                        </div>
                    )}

                    {!kontextLaeuft && !!oeffnung && (
                        <>
                            {/* ─── Frage ─────────────────────────────────── */}
                            {paket && paket.art !== 'koordinierung' && (
                                <textarea
                                    value={frage}
                                    onChange={e => setFrage(e.target.value)}
                                    rows={3}
                                    placeholder={paket.art === 'plan'
                                        ? 'Was möchten Sie wissen? Zum Beispiel: Welche Arbeitsschritte fehlen zwischen Rohbau und Ausbau?'
                                        : 'Was möchten Sie wissen? Zum Beispiel: Was gehört bei einer Einzelvergabe in die Baustellenordnung?'}
                                    className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-200"
                                />
                            )}

                            <button
                                type="button"
                                onClick={() => void frageStellen()}
                                disabled={laeuft}
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-violet-600 rounded-xl hover:bg-violet-700 disabled:opacity-40"
                            >
                                {laeuft
                                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Einen Moment …</>
                                    : <><Sparkles className="w-4 h-4" /> {stand ? 'Noch einmal fragen' : 'Vorschläge holen'}</>}
                            </button>

                            {/* ─── Lage ──────────────────────────────────── */}
                            <div className={`border rounded-xl px-4 py-3 text-sm ${LAGE_FARBE[zusammenfassung.lage]}`}>
                                <p className="font-bold">{zusammenfassung.ueberschrift}</p>
                                <p className="mt-0.5 text-[13px]">{zusammenfassung.text}</p>
                            </div>

                            {/* Nur-Ansicht/Freigabe: fragen ja, übernehmen nein. */}
                            {!darfUebernehmen && oeffnung.sperrgrund && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-[13px] text-amber-900 flex items-start gap-2">
                                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span className="whitespace-pre-line">{oeffnung.sperrgrund}</span>
                                </div>
                            )}

                            {aktionsfehler && (
                                <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800 flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span className="flex-1 whitespace-pre-line">{aktionsfehler}</span>
                                </div>
                            )}
                            {meldung && (
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-800 flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>{meldung}</span>
                                </div>
                            )}

                            {/* ═══ PLAN — nur Anzeige, mit Sprung ═════════ */}
                            {planAntwort && (
                                <div className="space-y-3">
                                    {planAntwort.antwort && (
                                        <Kasten>
                                            <p className="text-sm text-slate-700 whitespace-pre-line">{planAntwort.antwort}</p>
                                        </Kasten>
                                    )}

                                    {planAntwort.luecken.map((l, i) => (
                                        <Kasten key={`luecke-${i}`}>
                                            <p className="text-[11px] font-black uppercase tracking-wide text-slate-400">
                                                {l.schweregrad === 'fehler' ? 'Fehler' : l.schweregrad === 'warnung' ? 'Warnung' : 'Hinweis'}
                                            </p>
                                            <p className="text-sm font-bold text-slate-900 mt-0.5">{l.thema}</p>
                                            <p className="text-[13px] text-slate-600 mt-0.5">{l.beschreibung}</p>
                                        </Kasten>
                                    ))}

                                    {planAntwort.fehlendeArbeitsschritte.map((s, i) => (
                                        <Kasten key={`schritt-${i}`}>
                                            <p className="text-[11px] font-black uppercase tracking-wide text-slate-400">
                                                Fehlender Arbeitsschritt
                                            </p>
                                            <p className="text-sm font-bold text-slate-900 mt-0.5">{s.bezeichnung}</p>
                                            <p className="text-[13px] text-slate-600 mt-0.5">
                                                {[s.gewerk, s.hauptabschnitt].filter(Boolean).join(' · ')}
                                            </p>
                                            {s.begruendung && <p className="text-[13px] text-slate-500 mt-1">{s.begruendung}</p>}
                                            <div className="flex items-center gap-2 mt-2">
                                                <button
                                                    type="button"
                                                    onClick={() => { onSprung?.('plan'); onSchliessen(); }}
                                                    className="text-[11px] font-bold text-blue-600 hover:underline"
                                                >
                                                    Im Plan nachsehen
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => kopiere(s.bezeichnung)}
                                                    className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:underline"
                                                >
                                                    <Copy className="w-3 h-3" /> Text kopieren
                                                </button>
                                            </div>
                                        </Kasten>
                                    ))}

                                    {planAntwort.fehlendeGefaehrdungen.map((g, i) => (
                                        <Kasten key={`gef-${i}`}>
                                            <p className="text-[11px] font-black uppercase tracking-wide text-slate-400">
                                                Fehlende Gefährdung
                                                <span className={`ml-2 ${PRIO_FARBE[g.prioritaet]}`}>{g.prioritaet}</span>
                                            </p>
                                            <p className="text-sm font-bold text-slate-900 mt-0.5">{g.gefaehrdung}</p>
                                            {g.arbeitsschritt && (
                                                <p className="text-[13px] text-slate-600 mt-0.5">zu: {g.arbeitsschritt}</p>
                                            )}
                                            {g.massnahme && <p className="text-[13px] text-slate-500 mt-1">{g.massnahme}</p>}
                                            <div className="flex items-center gap-2 mt-2">
                                                <button
                                                    type="button"
                                                    onClick={() => { onSprung?.('plan'); onSchliessen(); }}
                                                    className="text-[11px] font-bold text-blue-600 hover:underline"
                                                >
                                                    Im Plan nachsehen
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => kopiere([g.gefaehrdung, g.massnahme].filter(Boolean).join('\n'))}
                                                    className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:underline"
                                                >
                                                    <Copy className="w-3 h-3" /> Text kopieren
                                                </button>
                                            </div>
                                        </Kasten>
                                    ))}

                                    {anzahl > 0 && (
                                        <p className="text-[11px] text-slate-400 px-1">
                                            Der Assistent schreibt nicht in den SiGe-Plan. Was Sie übernehmen wollen,
                                            tragen Sie im Plan-Editor ein. Dort steht auch die Fassung, auf die sich
                                            der Nachweis später beruft.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* ═══ KOORDINIERUNG — Vorschau und Übernahme ══ */}
                            {koordAntwort && paket && paket.art === 'koordinierung' && (
                                <div className="space-y-3">
                                    {koordAntwort.hinweise.map((h, i) => (
                                        <Kasten key={`hinweis-${i}`}>
                                            <p className="text-[13px] text-slate-600">{h}</p>
                                        </Kasten>
                                    ))}

                                    {geplant.map(g => {
                                        const schonDa = uebernommenePaare.includes(g.id);
                                        const waehlbar = g.befund === 'neu' && !schonDa && darfUebernehmen;
                                        return (
                                            <div
                                                key={g.id}
                                                className={`border rounded-xl p-3 ${
                                                    g.befund === 'neu' && !schonDa
                                                        ? 'bg-white border-slate-200'
                                                        : 'bg-slate-100 border-slate-200'
                                                }`}
                                            >
                                                <Haken
                                                    an={gewaehltePaare.includes(g.id)}
                                                    aus={!waehlbar}
                                                    onWechsel={() => setGewaehltePaare(m => wechsle(m, g.id))}
                                                    text={
                                                        <>
                                                            <span className="block text-[11px] font-black uppercase tracking-wide text-slate-400">
                                                                {g.vorschlag.a} × {g.vorschlag.b}
                                                                <span className={`ml-2 ${PRIO_FARBE[g.vorschlag.prioritaet]}`}>
                                                                    {g.vorschlag.prioritaet}
                                                                </span>
                                                            </span>
                                                            <span className="block text-sm font-bold text-slate-900 mt-0.5">
                                                                {g.vorschlag.gefaehrdung}
                                                            </span>
                                                            <span className="block text-[13px] text-slate-600 mt-0.5">
                                                                {g.vorschlag.massnahme}
                                                            </span>
                                                        </>
                                                    }
                                                />
                                                {g.vorschlag.begruendung && (
                                                    <p className="text-[12px] text-slate-500 mt-1.5 pl-6">{g.vorschlag.begruendung}</p>
                                                )}
                                                {g.vorschlag.vorschriften.length > 0 && (
                                                    // Bewusst nur zur Beurteilung: eine Fundstelle, die ein
                                                    // Modell geraten hat, wandert nicht in den Bericht.
                                                    <p className="text-[12px] text-slate-400 mt-1 pl-6">
                                                        Genannte Fundstellen (nicht übernommen): {g.vorschlag.vorschriften.join(', ')}
                                                    </p>
                                                )}
                                                <p className={`text-[12px] mt-1.5 pl-6 ${
                                                    schonDa ? 'text-emerald-700 font-bold' : 'text-slate-500'
                                                }`}>
                                                    {schonDa ? 'Übernommen: steht als unbestätigte Bewertung in der Liste.' : g.hinweis}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* ═══ HANDBUCH — Vorschau und Entwurf ═════════ */}
                            {handbuchAntwort && offeneOrdnung && (
                                <div className="space-y-3">
                                    {handbuchAntwort.hinweise.map((h, i) => (
                                        <Kasten key={`hb-hinweis-${i}`}>
                                            <p className="text-[13px] text-slate-600">{h}</p>
                                        </Kasten>
                                    ))}
                                    {handbuchAntwort.fehlendeAngaben.map((f, i) => (
                                        <Kasten key={`hb-fehlt-${i}`}>
                                            <p className="text-[11px] font-black uppercase tracking-wide text-slate-400">
                                                {f.kapitel || 'Fehlende Angabe'}
                                            </p>
                                            <p className="text-[13px] text-slate-600 mt-0.5">{f.hinweis}</p>
                                        </Kasten>
                                    ))}

                                    {uebernommeneOrdnung && (
                                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-[13px] text-emerald-900">
                                            Übernommen: die Punkte stehen jetzt im ENTWURF des Handbuchs.
                                            Der blaue Balken dort speichert sie.
                                        </div>
                                    )}

                                    {offeneOrdnung.anzahl === 0 && anzahl > 0 && (
                                        // Entwarnung nur bei vollständiger Antwort — bei einer
                                        // gekappten wurde der Ordnungsteil verworfen, und die
                                        // Leere sagt dann nichts über den Stand aus.
                                        <div className={`border rounded-xl p-3 ${
                                            leereOrdnung.ton === 'warnung'
                                                ? 'bg-amber-50 border-amber-200'
                                                : 'bg-white border-slate-200'
                                        }`}>
                                            <p className={`text-[13px] ${
                                                leereOrdnung.ton === 'warnung' ? 'text-amber-900' : 'text-slate-500'
                                            }`}>
                                                {leereOrdnung.text}
                                            </p>
                                        </div>
                                    )}

                                    {!uebernommeneOrdnung && offeneOrdnung.anzahl > 0 && (
                                        <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-2.5">
                                            <p className="text-[11px] font-black uppercase tracking-wide text-slate-400">
                                                Vorschlag zur Baustellenordnung
                                            </p>
                                            {offeneOrdnung.felder.map(f => (
                                                <Haken
                                                    key={f}
                                                    an={gewaehlteFelder.includes(f)}
                                                    aus={!darfUebernehmen}
                                                    onWechsel={() => setGewaehlteFelder(m => wechsle(m, f))}
                                                    text={
                                                        <>
                                                            <span className="block text-[11px] font-black uppercase tracking-wide text-slate-400">
                                                                {ORDNUNG_FELD_LABEL[f as OrdnungFeld]}
                                                            </span>
                                                            <span className="block">{handbuchAntwort.ordnung[f as OrdnungFeld]}</span>
                                                        </>
                                                    }
                                                />
                                            ))}
                                            {offeneOrdnung.regeln.map(r => (
                                                <Haken
                                                    key={`regel-${r}`}
                                                    an={gewaehlteRegeln.includes(r)}
                                                    aus={!darfUebernehmen}
                                                    onWechsel={() => setGewaehlteRegeln(m => wechsle(m, r))}
                                                    text={
                                                        <>
                                                            <span className="text-slate-400">Regel: </span>{r}
                                                            {erweiterungen.has(r) && (
                                                                <span className="block text-[12px] text-amber-700 mt-0.5">
                                                                    Schreibt „{erweiterungen.get(r)}" fort; beide Zeilen
                                                                    stünden danach im Entwurf.
                                                                </span>
                                                            )}
                                                        </>
                                                    }
                                                />
                                            ))}
                                            {offeneOrdnung.notfallschritte.map(s => (
                                                <Haken
                                                    key={`schritt-${s}`}
                                                    an={gewaehlteSchritte.includes(s)}
                                                    aus={!darfUebernehmen}
                                                    onWechsel={() => setGewaehlteSchritte(m => wechsle(m, s))}
                                                    text={
                                                        <>
                                                            <span className="text-slate-400">Notfallschritt: </span>{s}
                                                            {erweiterungen.has(s) && (
                                                                <span className="block text-[12px] text-amber-700 mt-0.5">
                                                                    Schreibt „{erweiterungen.get(s)}" fort; beide Zeilen
                                                                    stünden danach im Entwurf.
                                                                </span>
                                                            )}
                                                        </>
                                                    }
                                                />
                                            ))}
                                            <p className="text-[12px] text-slate-400">
                                                Vorhandene Regeln bleiben stehen; Ausgewähltes wird angehängt.
                                                Ausgefüllte Freitextfelder werden nie überschrieben.
                                            </p>
                                        </div>
                                    )}

                                    {/* ─── Baustelleneinrichtung ──────────────
                                        Eigener Kasten und eigene Auswahl: die
                                        Ordnung haengt am Aushang fuer den
                                        Bauzaun, die Einrichtung an Kapitel 3.
                                        Wer das eine ergaenzen laesst, soll das
                                        andere unangetastet lassen koennen. */}
                                    {uebernommeneEinrichtung && (
                                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-[13px] text-emerald-900">
                                            Übernommen: die Baustelleneinrichtung steht jetzt im ENTWURF
                                            des Handbuchs. Der blaue Balken dort speichert sie.
                                        </div>
                                    )}

                                    {!uebernommeneEinrichtung && offeneEinrichtung
                                        && offeneEinrichtung.anzahl > 0 && (
                                        <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-2.5">
                                            <p className="text-[11px] font-black uppercase tracking-wide text-slate-400">
                                                Vorschlag zur Baustelleneinrichtung
                                            </p>
                                            {offeneEinrichtung.punkte.map(punkt => (
                                                <Haken
                                                    key={`einr-${punkt.feld}`}
                                                    an={gewaehlteEinrichtung.includes(punkt.feld)}
                                                    aus={!darfUebernehmen}
                                                    onWechsel={() => setGewaehlteEinrichtung(
                                                        m => wechsle(m, punkt.feld),
                                                    )}
                                                    text={
                                                        <>
                                                            <span className="block text-[11px] font-black uppercase tracking-wide text-slate-400">
                                                                {EINRICHTUNG_FELD_LABEL[punkt.feld]}
                                                                {punkt.art === 'ergaenzen' && (
                                                                    <span className="ml-1.5 text-amber-600">ergänzt</span>
                                                                )}
                                                            </span>
                                                            <span className="block whitespace-pre-line">{punkt.text}</span>
                                                            {punkt.art === 'ergaenzen' && (
                                                                <span className="block text-[12px] text-amber-700 mt-0.5">
                                                                    Wird unter den vorhandenen Text gesetzt; der bleibt
                                                                    unverändert stehen.
                                                                </span>
                                                            )}
                                                        </>
                                                    }
                                                />
                                            ))}
                                            <p className="text-[12px] text-slate-400">
                                                Landet in Kapitel 3 neben dem Lageplan. Was dort nicht auf die
                                                Seite passt, schneidet der Druck ab; die Längenanzeige im
                                                Handbuch zeigt es.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ─── Fuß: Übernahme ───────────────────────────────────── */}
                {darfUebernehmen && (
                    neuePaare.length > 0
                    || (offeneOrdnung && offeneOrdnung.anzahl > 0 && !uebernommeneOrdnung)
                    || (offeneEinrichtung && offeneEinrichtung.anzahl > 0 && !uebernommeneEinrichtung)
                ) && (
                    <div className="bg-white border-t border-slate-200 px-4 py-3 flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={alleWaehlen}
                            className="text-[11px] font-bold text-slate-500 hover:underline"
                        >
                            Alle auswählen
                        </button>
                        <span className="text-[11px] text-slate-400">{anzahlGewaehlt} gewählt</span>
                        <button
                            type="button"
                            onClick={() => (paket && paket.art === 'koordinierung'
                                ? void uebernimmPaare()
                                : void uebernimmHandbuch())}
                            disabled={laeuft || anzahlGewaehlt === 0}
                            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                        >
                            <Check className="w-4 h-4" />
                            {paket && paket.art === 'koordinierung' ? 'Übernehmen' : 'In den Entwurf'}
                        </button>
                    </div>
                )}
            </aside>
        </div>
    );
};
